import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { timeAgo } from "../lib/time";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  KeyRound, Plus, Copy, Check, Trash2, X, ShieldAlert,
  Webhook, Settings2, BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";

const CODE_SNIPPET = (baseUrl, key) => `curl -X POST "${baseUrl}/api/public/tickets" \\
  -H "X-API-Key: ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": "Website contact form",
    "message": "Hi team, I need help with my hosting.",
    "customer_name": "Jane Doe",
    "customer_email": "jane@example.com"
  }'`;

function CreateKeyModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState("write");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setScope("write"); setWebhookUrl(""); }
  }, [open]);

  if (!open) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), scope };
      if (webhookUrl.trim()) payload.webhook_url = webhookUrl.trim();
      const { data } = await api.post("/admin/api-keys", payload);
      toast.success("API key created");
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error("Could not create", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const scopeOpt = (value, title, desc) => (
    <label
      data-testid={`api-key-scope-${value}`}
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer text-sm ${
        scope === value ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
      }`}>
      <input type="radio" name="apikey-scope" className="hidden"
             checked={scope === value} onChange={() => setScope(value)} />
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className={`text-[11px] mt-0.5 ${scope === value ? "text-white/80" : "text-slate-500"}`}>{desc}</div>
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid="api-key-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Public API</div>
            <h3 className="font-display text-lg font-bold text-slate-900">Create API key</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="beacon-label">Name</label>
          <input className="beacon-input" placeholder='e.g. "Website contact form" or "Internal CRM"'
                 data-testid="api-key-name-input"
                 autoFocus
                 value={name} onChange={(e) => setName(e.target.value)} />
          <div className="text-[11px] text-slate-500 mt-1">
            Shown in the ticket source and request logs.
          </div>
        </div>
        <div>
          <label className="beacon-label">Scope</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {scopeOpt("read", "Read-only", "Call GET endpoints only. Safe for status widgets.")}
            {scopeOpt("write", "Read + write", "Can create tickets, replies, upload attachments.")}
          </div>
        </div>
        <div>
          <label className="beacon-label flex items-center gap-1.5"><Webhook className="w-3.5 h-3.5" /> Webhook URL <span className="text-slate-400 font-normal text-xs">(optional)</span></label>
          <input className="beacon-input" placeholder="https://your-app.com/pixel/webhook"
                 data-testid="api-key-webhook-input"
                 value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
          <div className="text-[11px] text-slate-500 mt-1">
            POSTed on <code>ticket.created</code>, <code>ticket.updated</code> (status/assignee) and <code>ticket.replied</code>.
            Signed with HMAC-SHA256 via a secret we'll reveal once below.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || !name.trim()}
                  data-testid="api-key-create-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Creating…" : "Generate key"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function RevealKeyModal({ created, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  if (!created) return null;

  const copy = async (value, setter) => {
    try {
      await navigator.clipboard.writeText(value);
      setter(true);
      setTimeout(() => setter(false), 1800);
    } catch (_) {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="api-key-reveal-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">New API key</div>
            <h3 className="font-display text-lg font-bold text-slate-900">{created.name}</h3>
            {created.scope && (
              <span className="inline-block mt-1 beacon-badge bg-slate-100 text-slate-700 border-slate-200 text-[10px]">
                scope: {created.scope}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            This is the only time this key {created.webhook_secret ? "and webhook secret are" : "is"} shown.
            Copy them now and store in your external app's secret manager — we only keep hashed/masked copies on our side.
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">API key</div>
          {created.api_key ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs sm:text-sm bg-slate-900 text-emerald-300 rounded-lg px-3 py-2.5 break-all"
                  data-testid="api-key-reveal-value">
              {created.api_key}
            </code>
            <Button variant="outline" onClick={() => copy(created.api_key, setCopied)} data-testid="api-key-copy-btn">
              {copied ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Copied</>
                      : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
            </Button>
          </div>
          ) : (
            <div className="text-xs text-slate-500">
              The API key itself was shown at creation and can't be viewed again. Only the webhook secret has been rotated.
            </div>
          )}
        </div>
        {created.webhook_secret && (
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Webhook secret (HMAC-SHA256)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs sm:text-sm bg-slate-900 text-amber-300 rounded-lg px-3 py-2.5 break-all"
                    data-testid="api-key-webhook-secret">
                {created.webhook_secret}
              </code>
              <Button variant="outline" onClick={() => copy(created.webhook_secret, setCopiedSecret)}>
                {copiedSecret ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Copied</>
                        : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Verify delivery with the <code>X-Pixel-Signature: sha256=…</code> header.
            </div>
          </div>
        )}
        {created.api_key && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Quick start</div>
          <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-800 overflow-x-auto font-mono"
               data-testid="api-key-reveal-snippet">
{CODE_SNIPPET(window.location.origin, created.api_key)}
          </pre>
        </div>
        )}
        <div className="flex items-center justify-end pt-2 border-t border-slate-100">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="api-key-reveal-done-btn"
                  onClick={onClose}>
            I've copied the secrets
          </Button>
        </div>
      </div>
    </div>
  );
}

function WebhookEditModal({ row, onClose, onSaved }) {
  const [url, setUrl] = useState(row?.webhook_url || "");
  const [saving, setSaving] = useState(false);

  if (!row) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/api-keys/${row.id}`, { webhook_url: url.trim() });
      toast.success("Webhook updated");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid="api-key-webhook-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Webhook</div>
            <h3 className="font-display text-lg font-bold text-slate-900">{row.name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="beacon-label">Delivery URL</label>
          <input className="beacon-input" placeholder="https://your-app.com/pixel/webhook"
                 value={url} onChange={(e) => setUrl(e.target.value)}
                 data-testid="api-key-webhook-url-input" />
          <div className="text-[11px] text-slate-500 mt-1">
            Clear to disable. A new signing secret is issued whenever the URL changes.
          </div>
          {row.last_webhook_at && (
            <div className="text-[11px] text-slate-500 mt-1">
              Last delivery: {timeAgo(row.last_webhook_at)} · status {row.last_webhook_status ?? "—"}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="api-key-webhook-save-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving…" : "Save webhook"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminApiKeys() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [revealing, setRevealing] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/api-keys");
      setRows(r.data || []);
    } catch (err) {
      toast.error("Could not load API keys", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (row) => {
    if (!window.confirm(`Revoke "${row.name}"? Any external app using this key will immediately fail.`)) return;
    try {
      await api.delete(`/admin/api-keys/${row.id}`);
      toast.success("API key revoked");
      load();
    } catch (err) {
      toast.error("Revoke failed", { description: formatApiError(err) });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Public API keys
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Let external apps raise tickets, add replies, check status and receive webhooks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/docs/api" target="_blank"
                data-testid="api-docs-link"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 h-10 text-sm font-semibold text-slate-700 hover:border-slate-400">
            <BookOpen className="w-4 h-4" /> API docs
          </Link>
          <Button onClick={() => setModalOpen(true)}
                  data-testid="api-key-new-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg h-10">
            <Plus className="w-4 h-4 mr-1.5" /> New API key
          </Button>
        </div>
      </div>

      <div className="beacon-card divide-y divide-slate-200">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading keys…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <KeyRound className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <div className="font-semibold text-slate-700">No API keys yet</div>
            <div className="text-xs text-slate-500 mt-1">Click <strong>New API key</strong> to issue one to an external app.</div>
          </div>
        ) : (
          rows.map((r) => {
            const revoked = !!r.revoked_at;
            const scope = r.scope || "write";
            return (
              <div key={r.id} data-testid={`api-key-row-${r.id}`}
                   className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                    revoked ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {r.name}
                      <span className={`beacon-badge text-[10px] ${
                        scope === "read" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-900 text-white border-slate-900"
                      }`}>
                        {scope}
                      </span>
                      {r.webhook_url && (
                        <span className="beacon-badge text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"
                              title={r.webhook_url}>
                          <Webhook className="w-3 h-3" /> webhook
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{r.key_prefix}</div>
                    {r.webhook_url && (
                      <div className="text-[11px] text-slate-500 truncate font-mono mt-0.5">{r.webhook_url}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-right text-[11px] text-slate-500 leading-tight">
                    <div>Used {r.usage_count || 0} times</div>
                    <div>Last used {r.last_used_at ? timeAgo(r.last_used_at) : "—"}</div>
                    {r.webhook_url && r.last_webhook_at && (
                      <div>Webhook {timeAgo(r.last_webhook_at)} · {r.last_webhook_status ?? "—"}</div>
                    )}
                  </div>
                  {revoked ? (
                    <span className="beacon-badge bg-slate-100 text-slate-500 border-slate-200">revoked</span>
                  ) : (
                    <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200">
                      <span className="beacon-dot bg-emerald-500" /> active
                    </span>
                  )}
                  {!revoked && (
                    <>
                      <Button size="sm" variant="outline"
                              onClick={() => setEditingWebhook(r)}
                              data-testid={`api-key-webhook-${r.id}`}>
                        <Settings2 className="w-3.5 h-3.5 mr-1" /> Webhook
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => revoke(r)}
                              data-testid={`api-key-revoke-${r.id}`}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <CreateKeyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(data) => { setRevealing(data); load(); }}
      />
      <RevealKeyModal
        created={revealing}
        onClose={() => setRevealing(null)}
      />
      <WebhookEditModal
        row={editingWebhook}
        onClose={() => setEditingWebhook(null)}
        onSaved={(data) => {
          if (data.webhook_secret) {
            setRevealing({
              name: editingWebhook?.name || "Webhook",
              webhook_secret: data.webhook_secret,
              api_key: "", // we won't reveal the api_key here
              scope: editingWebhook?.scope,
            });
          }
          load();
        }}
      />
    </div>
  );
}
